<template>
  <v-flex
    id="clothing"
    class="question"
  >
    <p class="mb-0 text-uppercase">
      {{ $t('sheet.sheet_two.hint') }}
    </p>
    <h2 class="header">
      {{ getClothingQuestions.label }}
    </h2>
    <ul class="questions-list">
      <li
        v-if="getClothingQuestions.hasOwnProperty('need_clothes')"
        class="mt-4"
      >
        <p
          class="header"
          :class="{ required: getClothingQuestions.need_clothes.required }"
        >
          {{ getClothingQuestions.need_clothes.label }}
        </p>
        <ConfirmButtons
          v-model="needClothes"
          v-validate="{ required: getClothingQuestions.need_clothes.required }"
          string
          :error-messages="errors.collect('needClothes')"
          :data-vv-as="' '"
          :data-vv-name="'needClothes'"
          :disabled="readOnly"
        />
      </li>
      <li
        v-if="
          needClothes === 'yes' && getClothingQuestions.hasOwnProperty('chosen_clothes_members')
        "
        class="mt-4"
      >
        <p
          class="header custom lA mb-0"
          :class="{ required: getClothingQuestions.chosen_clothes_members.required }"
        >
          {{ getClothingQuestions.chosen_clothes_members.label }}
        </p>
        <v-layout wrap>
          <v-flex
            v-for="(member, index) in representatives"
            :key="index"
            lg3
            md4
            sm6
            xs12
          >
            <v-checkbox
              v-model="chosenClothesMembers"
              v-validate="{ required: getClothingQuestions.chosen_clothes_members.required }"
              multiple
              :value="member.id"
              :error="errors.has('chosenClothesMembers')"
              :error-messages="
                index + 1 === representatives.length ? errors.collect('chosenClothesMembers') : null
              "
              :data-vv-as="' '"
              :data-vv-name="'chosenClothesMembers'"
              :disabled="readOnly"
            >
              <template slot="label">
                <div>
                  <p class="mb-0">
                    {{ member.newName }}
                  </p>
                  <p class="f-05 fw-400 mb-0">
                    {{ $t('sheet.sheet_two.original') | addColons }} {{ member.name }}
                  </p>
                </div>
              </template>
            </v-checkbox>
          </v-flex>
        </v-layout>
        <template v-if="showClothingForm">
          <ClothingForm
            v-for="(member, index) in chosenClothesDetails"
            :key="index"
            :index="index"
            :member="member"
            :new-name="findNewName(member.id)"
            :read-only="readOnly"
            :chosen-clothes-details="getClothingQuestions.chosen_clothes_details"
          />
        </template>
      </li>
      <li
        v-if="getClothingQuestions.hasOwnProperty('need_footwear')"
        class="mt-4"
      >
        <p
          class="header"
          :class="{ required: getClothingQuestions.need_footwear.required }"
        >
          {{ getClothingQuestions.need_footwear.label }}
        </p>
        <ConfirmButtons
          v-model="needFootwear"
          v-validate="{ required: getClothingQuestions.need_footwear.required }"
          string
          :error-messages="errors.collect('needFootwear')"
          :data-vv-as="' '"
          :data-vv-name="'needFootwear'"
          :disabled="readOnly"
        />
      </li>
      <li
        v-if="
          needFootwear === 'yes' && getClothingQuestions.hasOwnProperty('chosen_footwear_members')
        "
        class="mt-4"
      >
        <p
          class="header custom lA mb-0"
          :class="{ required: getClothingQuestions.chosen_footwear_members.required }"
        >
          {{ getClothingQuestions.chosen_footwear_members.label }}
        </p>
        <v-layout wrap>
          <v-flex
            v-for="(member, index) in representatives"
            :key="index"
            lg3
            md4
            sm6
            xs12
          >
            <v-checkbox
              v-model="chosenFootwearMembers"
              v-validate="{ required: getClothingQuestions.chosen_footwear_members.required }"
              multiple
              :value="member.id"
              :error="errors.has('chosenFootwearMembers')"
              :error-messages="
                index + 1 === representatives.length
                  ? errors.collect('chosenFootwearMembers')
                  : null
              "
              :data-vv-as="' '"
              :data-vv-name="'chosenFootwearMembers'"
              :disabled="readOnly"
            >
              <template slot="label">
                <div>
                  <p class="mb-0">
                    {{ member.newName }}
                  </p>
                  <p class="f-05 fw-400 mb-0">
                    {{ $t('sheet.sheet_two.original') | addColons }} {{ member.name }}
                  </p>
                </div>
              </template>
            </v-checkbox>
          </v-flex>
        </v-layout>
        <template v-if="showFootwear">
          <FootwearForm
            v-for="(member, index) in chosenFootwearDetails"
            :key="index"
            :index="index"
            :member="member"
            :new-name="findNewName(member.id)"
            :read-only="readOnly"
            :chosen-footwear-details="getClothingQuestions.chosen_footwear_details"
          />
        </template>
      </li>
    </ul>
    <v-divider class="my-4" />
  </v-flex>
</template>

<script>
import ConfirmButtons from '@/components/widgets/sheet/ConfirmButtons';
import ClothingForm from '@/components/widgets/sheet/sheetTwo/ClothingForm';
import FootwearForm from '@/components/widgets/sheet/sheetTwo/FootwearForm';
import filters from '@/constants/filters';
import { createHelpers } from 'vuex-map-fields';
import { mapGetters } from 'vuex';

import sheetResourcesGetter from '@/mixins/sheet/sheetResourcesGetter';
import sheetValidationRules from '@/mixins/sheet/sheetValidationRules';

import has from 'lodash.has';

const { mapFields } = createHelpers({
    getterType: 'sheet/getClothingFields',
    mutationType: 'sheet/updateClothingField'
});

export default {
    name: 'SheetClothing',
    components: { ConfirmButtons, ClothingForm, FootwearForm },
    filters: {
        ...filters
    },
    mixins: [sheetResourcesGetter, sheetValidationRules],
    inject: ['$validator'],
    props: {
        readOnly: {
            default: false,
            type: Boolean
        }
    },
    data () {
        return {
            emptyMembersData: {
                complete: false
            }
        };
    },
    computed: {
        ...mapFields([
            'needClothes',
            'chosenClothesMembers',
            'chosenClothesDetails',
            'needFootwear',
            'chosenFootwearMembers',
            'chosenFootwearDetails'
        ]),
        representatives () {
            return this.$store.getters['sheet/getNewNames'];
        },
        ...mapGetters('sheet', ['getClothingQuestions']),
        showClothingForm () {
            return has(this.getClothingQuestions, 'chosen_clothes_details');
        },
        showFootwear () {
            return has(this.getClothingQuestions, 'chosen_footwear_details');
        }
    },
    watch: {
        chosenClothesMembers: function (val) {
            this.chosenClothesDetails = this.chosenClothesDetails.filter((item) => {
                return val.includes(item.id);
            });
            this.manageDetailsList(val, this.chosenClothesDetails);
        },
        chosenFootwearMembers: function (val) {
            this.chosenFootwearDetails = this.chosenFootwearDetails.filter((item) => {
                return val.includes(item.id);
            });
            this.manageDetailsList(val, this.chosenFootwearDetails);
        },
        needClothes (val) {
            if (val === 'no') {
                this.$nextTick(() => {
                    this.chosenClothesMembers = [];
                });
            }
        },
        needFootwear (val) {
            if (val === 'no') {
                this.$nextTick(() => {
                    this.chosenFootwearMembers = [];
                });
            }
        }
    },
    methods: {
        manageDetailsList (val, arr) {
            if (val.length > arr.length) {
                val.forEach((el) => {
                    if (!arr.find((obj) => obj.id === el)) {
                        arr.push({
                            id: el,
                            chosenList: []
                        });
                    }
                });
            }
        },
        findNewName (id) {
            return this.representatives.find((x) => x.id === id).newName;
        }
    }
};
</script>

<style scoped></style>
